/**
 * TagBasedRecommendations — the product page "You may also like" carousel.
 *
 * Ranks the prebuilt recommendations-index.json (in-stock products only, ~180 KB,
 * published to S3 by the import script) against the current product's match tags
 * using assets/recommendation-matching.js. Then one Storefront API request checks
 * the top picks: anything sold or put on hold since the index was built is dropped,
 * and each card gets its current photo.
 *
 * Cards are clones of #product-item-template — the same markup the search and
 * collection grids use — laid out in Dawn's <slider-component>.
 *
 * On any failure (no tags, index unreachable, nothing related) the element
 * removes itself and un-hides Shopify's native related-products section, which
 * sits below it in templates/product.json as the fallback.
 *
 * A recommendation whose thumbnail is missing, or fails to load, is not shown.
 */

// Same Storefront API version as graphql-product-manager.js.
const STOREFRONT_API_VERSION = '2024-10';
// A live check still pending after this long is abandoned for the index as it is.
const LIVE_CHECK_TIMEOUT_MS = 3000;

class TagBasedRecommendations extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
  }

  connectedCallback() {
    // Lazy-load: the carousel is far below the fold on most product pages.
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * recommendation-matching.js is an ES module and search-index-loader.js is
   * deferred, so neither is guaranteed to have run by the time the observer
   * fires on a short page. Poll briefly rather than falling back on a race.
   */
  async waitForDependencies(timeoutMs = 3000) {
    const ready = () => window.RecommendationMatching && window.SearchIndexLoader;
    const deadline = Date.now() + timeoutMs;

    while (!ready() && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return ready();
  }

  async loadRecommendations() {
    if (!(await this.waitForDependencies())) {
      console.error('Tag-based recommendations: matcher / index loader never became available');
      this.fallbackToNative();
      return;
    }
    const matcher = window.RecommendationMatching;

    const tags = (this.dataset.productTags || '').split('|||').filter((tag) => tag.trim());
    const keys = matcher.parseMatchKeys(tags);
    const current = {
      ...keys,
      // The model- tag squashes the Model's words together; recover them from
      // the title so the ranker can match "Les Paul Standard" against
      // "Les Paul Classic Custom Lite".
      mt: matcher.recoverModelTokens(this.dataset.productTitle, keys.model),
      family: matcher.familyOf(this.dataset.productType),
      handle: this.dataset.productHandle,
      id: this.dataset.productId,
    };

    // Nothing to match on — every product should carry at least a maker- tag,
    // but the Access data has gaps.
    if (!current.model && !current.subtype && !current.maker && !current.kw.length) {
      this.fallbackToNative();
      return;
    }

    try {
      const pool = await window.SearchIndexLoader.getIndex(window.RECOMMENDATIONS_INDEX_URL);
      const limit = parseInt(this.dataset.productsToShow, 10) || 12;
      // Rank twice the limit: the live check drops whatever has sold since the
      // index was built, and a product with no photo gives its slot to the next
      // one down.
      const candidates = matcher.rankRecommendations(pool, current, limit * 2);
      const products = (await this.checkLiveState(candidates))
        .filter((product) => product.image)
        .slice(0, limit);

      if (!products.length) {
        this.fallbackToNative();
        return;
      }

      this.renderCarousel(products);
    } catch (error) {
      console.error('Tag-based recommendations error:', error);
      this.fallbackToNative();
    }
  }

  /**
   * Hand the slot back to Shopify's native recommendations: drop the style tag
   * that was hiding them, then remove ourselves.
   */
  fallbackToNative() {
    const hideStyle = document.getElementById(this.dataset.hideNativeStyleId);
    if (hideStyle) hideStyle.remove();
    this.remove();
  }

  /**
   * Ask Shopify about the candidates in one request (liveStateQuery in
   * recommendation-matching.js). That way nothing sold since the last import run
   * is recommended, and every card gets its current photo. If the request fails,
   * carry on with the index as it is; dropSlide() still catches a stale photo.
   */
  async checkLiveState(candidates) {
    const matcher = window.RecommendationMatching;
    if (!candidates.length || !window.SHOPIFY_STOREFRONT_TOKEN) return candidates;

    try {
      const response = await fetch(`https://${window.Shopify.shop}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.SHOPIFY_STOREFRONT_TOKEN,
        },
        body: JSON.stringify({ query: matcher.liveStateQuery(candidates.map((product) => product.handle)) }),
        signal: AbortSignal.timeout(LIVE_CHECK_TIMEOUT_MS),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      if (result.errors || !result.data) throw new Error(result.errors?.[0]?.message || 'no data');

      return matcher.applyLiveState(candidates, result.data);
    } catch (error) {
      console.warn('Tag-based recommendations: live check failed, using the index as is:', error);
      return candidates;
    }
  }

  /**
   * A thumbnail that fails to load. The live check normally supplies the current
   * photo, so this is the fallback for when that check fails and the index's
   * photo is stale: the product was re-imported after the last build, which gave
   * it new image URLs. Drop the card rather than show it broken, and re-count
   * the slider's pages. Lazy thumbnails load a few cards ahead of the viewer, so
   * this happens off-screen.
   */
  dropSlide(slide) {
    slide.remove();
    if (!this.querySelector('.slider__slide')) {
      this.fallbackToNative();
      return;
    }
    this.querySelector('slider-component')?.resetPages?.();
  }

  renderCarousel(products) {
    const template = document.getElementById('product-item-template');
    if (!template) {
      console.error('Tag-based recommendations: #product-item-template is missing');
      this.fallbackToNative();
      return;
    }

    const sectionId = this.dataset.sectionId;
    const heading = this.dataset.heading || 'You may also like';
    const headingSize = this.dataset.headingSize || 'h2';

    // Build the skeleton via innerHTML so <slider-component> is upgraded by the
    // parser (constructing one with document.createElement would run its
    // constructor while it is still empty, and it bails out without wiring the
    // buttons). It needs [id^="Slider-"], the two buttons and the counter to be
    // present at upgrade time — the slides are appended below and picked up by
    // resetPages().
    this.innerHTML = `
      <slider-component class="slider-mobile-gutter tag-recs__slider">
        <h2 class="tag-recs__heading inline-richtext ${headingSize}"></h2>
        <ul
          id="Slider-${sectionId}"
          class="tag-recs__list slider slider--desktop slider--tablet grid--peek"
          role="list"
        ></ul>
        <div class="slider-buttons">
          <button
            type="button"
            name="previous"
            class="slider-button slider-button--prev"
            aria-label="Slide left"
          >
            <span class="svg-wrapper">${this.arrowIcon()}</span>
          </button>
          <div class="slider-counter caption">
            <span class="slider-counter--current">1</span>
            <span aria-hidden="true"> / </span>
            <span class="slider-counter--total">${products.length}</span>
          </div>
          <button
            type="button"
            name="next"
            class="slider-button slider-button--next"
            aria-label="Slide right"
          >
            <span class="svg-wrapper">${this.arrowIcon()}</span>
          </button>
        </div>
      </slider-component>
    `;

    // The heading is merchant-authored inline_richtext; set it as text so a
    // stray tag can't inject markup.
    this.querySelector('.tag-recs__heading').textContent = heading;

    const list = this.querySelector('.tag-recs__list');
    const slides = document.createDocumentFragment();
    products.forEach((product, index) => {
      slides.appendChild(this.buildSlide(template, product, `${sectionId}-${index}`));
    });
    list.appendChild(slides);

    // Re-measure now that the slides exist (global.js:753).
    this.querySelector('slider-component')?.resetPages?.();
    this.classList.add('tag-recs--loaded');
  }

  /**
   * Clone #product-item-template and populate it from an index row.
   *
   * The field rules mirror the search grid's renderProductList() /
   * updateSingleProductDisplay() in product-grid-search-controller.js, so a card
   * here reads identically to the same product on the search page. The pool is
   * pre-filtered to in-stock, not-on-hold, so there is no sold/on-hold branch.
   */
  buildSlide(template, product, slideId) {
    const clone = template.content.cloneNode(true);
    const listItem = clone.querySelector('.product-item');

    listItem.id = `Slide-${slideId}`;
    listItem.classList.add('slider__slide', 'available');
    listItem.setAttribute('data-product-id', product.id);

    const link = listItem.querySelector('.product-link');
    if (link) {
      link.href = `/products/${product.handle}`;
      link.classList.remove('loading');
    }

    const name = listItem.querySelector('.product-name');
    if (name) name.textContent = product.title;

    const image = listItem.querySelector('.product-image-placeholder');
    if (image) {
      image.addEventListener('error', () => this.dropSlide(listItem), { once: true });
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';
      image.style.display = 'block';
    }

    const price = listItem.querySelector('.product-price');
    if (price) {
      if (product.showPrice === false || !product.price) {
        price.style.display = 'none';
      } else {
        price.textContent = `$${parseFloat(product.price).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      }
    }

    const justArrived = listItem.querySelector('.product-just-arrived');
    if (justArrived && (product.justArrived === true || product.justArrived === 'true')) {
      justArrived.style.display = 'block';
    }

    const promo = listItem.querySelector('.product-promo-headline');
    if (promo && product.promoHeadline) {
      promo.textContent = product.promoHeadline;
      promo.style.display = 'block';
    }

    const sku = listItem.querySelector('.sku-value');
    if (sku) sku.textContent = product.sku || 'No SKU';

    return clone;
  }

  /**
   * Dawn's icon-caret.svg, inlined. It is a DOWN chevron by design —
   * component-slider.css rotates it -90deg for next and +90deg for prev (and
   * scales it on hover). Any other glyph orientation gets rotated into
   * nonsense by those rules, so keep this identical to assets/icon-caret.svg.
   */
  arrowIcon() {
    return `
      <svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true" focusable="false">
        <path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/>
      </svg>
    `;
  }
}

customElements.define('tag-based-recommendations', TagBasedRecommendations);
